import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { ConnectedPosition } from "@angular/cdk/overlay";
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    ElementRef,
    Input,
    QueryList,
    ViewChild,
    forwardRef,
} from "@angular/core";
import { MAT_OPTION_PARENT_COMPONENT } from "@angular/material/core";
import { MatSelect, matSelectAnimations } from "@angular/material/select";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Subject, debounceTime, takeUntil } from "rxjs";
import { startWith } from "rxjs/operators";
import { SelectOptionComponent } from "./select-option/select-option.component";

@UntilDestroy()
@Component({
    selector: "dtm-ui-select-field",
    templateUrl: "./select-field.component.html",
    styleUrls: ["./select-field.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        role: "combobox",
        "aria-autocomplete": "none",
        "aria-haspopup": "listbox",
        "[class.mat-mdc-select]": "false",
        "[class.mat-mdc-select-disabled]": "false",
        "[class.mat-mdc-select-invalid]": "false",
        "[class.mat-mdc-select-required]": "false",
        "[class.mat-mdc-select-empty]": "false",
        "[class.mat-mdc-select-multiple]": "false",
        "[attr.id]": "id",
        "[attr.tabindex]": "tabIndex",
        "[attr.aria-controls]": "panelOpen ? id + '-panel' : null",
        "[attr.aria-expanded]": "panelOpen",
        "[attr.aria-label]": "ariaLabel || null",
        "[attr.aria-required]": "required.toString()",
        "[attr.aria-disabled]": "disabled.toString()",
        "[attr.aria-invalid]": "errorState",
        "[attr.aria-activedescendant]": "_getAriaActiveDescendant()",
        ngSkipHydration: "",
        "(keydown)": "_handleKeydown($event)",
        "(focus)": "_onFocus()",
        "(blur)": "_onBlur()",
    },
    animations: [matSelectAnimations.transformPanel],
    providers: [{ provide: MAT_OPTION_PARENT_COMPONENT, useExisting: forwardRef(() => SelectFieldComponent) }],
})
export class SelectFieldComponent extends MatSelect implements AfterContentInit {
    @ViewChild("trigger", { read: ElementRef }) public override trigger!: ElementRef;

    @ContentChildren(SelectOptionComponent, { descendants: true }) public override options!: QueryList<SelectOptionComponent>;

    @Input({ transform: coerceBooleanProperty }) public isClearable = true;
    @Input() public maxPanelHeight = "240px";

    public override _positions: ConnectedPosition[] = [
        {
            originX: "start",
            originY: "bottom",
            overlayX: "start",
            overlayY: "top",
            offsetY: 24,
            offsetX: -10,
        },
        {
            originX: "center",
            originY: "bottom",
            overlayX: "center",
            overlayY: "top",
            offsetY: 24,
            offsetX: -10,
        },
        {
            originX: "start",
            originY: "top",
            overlayX: "start",
            overlayY: "bottom",
            offsetY: -24,
            offsetX: -10,
        },
    ];

    private onChange$: Subject<any> | undefined;

    public get activeItem() {
        // eslint-disable-next-line no-underscore-dangle
        return this._keyManager?.activeItem;
    }

    protected get selectedValuesLabel(): string {
        return Array.isArray(this.selected) ? this.selected.map((item) => item.viewValue).join(", ") : "";
    }

    // NOTE: this is workaround for issue DTM-2129
    // Issue only occurs when in ng-content we will provide template with some structural directives (ex. *ngIf)
    // TODO: DTM-2129
    public override ngAfterContentInit() {
        super.ngAfterContentInit();

        // eslint-disable-next-line no-underscore-dangle
        const changesSubscription = this.options.changes.pipe(startWith(null), takeUntil(this._destroy)).subscribe(() => {
            setTimeout(() => {
                if (changesSubscription.closed) {
                    return;
                }

                super["_resetOptions"]();
                super["_initializeSelection"]();
            });
        });
    }

    public override registerOnChange(fn: (value: unknown) => void): void {
        this.onChange$?.complete();
        this.onChange$ = new Subject<unknown>();

        // NOTE: debounceTime(0) prevents 'select all' to trigger multiple updates
        this.onChange$.pipe(debounceTime(0), untilDestroyed(this)).subscribe(fn);

        // eslint-disable-next-line no-underscore-dangle
        this._onChange = (value) => this.onChange$?.next(value);
    }

    protected clearValue(event: Event) {
        event.stopPropagation();

        if (Array.isArray(this.value)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const newValue: any[] = [];

            this.options.forEach((option) => {
                if (option.disabled && this.value.includes(option.value)) {
                    newValue.push(option.value);
                }
            });

            this.value = newValue.length ? newValue : null;
        } else {
            this.value = null;
        }
    }

    protected selectAll() {
        this.options.forEach((option) => {
            if (!option.disabled) {
                option.select();
            }
        });
    }
}
